import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AlertBannerContext,
  API_RESPONSE_CODE,
  HandledGraphQLMutationError,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import {
  listSubscriptionsQuery,
  getSubscriptionQuery,
  createSubscriptionMutation,
  cancelSubscriptionMutation
} from "../../Graphql/querries/subscriptionQuery";
import {
  ListSubscriptionsResponse,
  GetSubscriptionResponse,
  CreateSubscriptionResponse,
  CancelSubscriptionResponse,
  ListSubscriptionsInput,
  GetSubscriptionInput,
  CreateSubscriptionInput,
  CancelSubscriptionInput
} from "../../Graphql/types/subscription";
import { RootState } from "../../store/store";
import {
  setSubscriptionList,
  setSubscriptionDetail
} from "../../store/slices/subscriptionSlice";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

/**
 * useGetSubscriptionList
 * Fetch all subscriptions for the current user/company
 */
export const useGetSubscriptionList = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<ListSubscriptionsResponse, Error, ListSubscriptionsInput>({
    mutationKey: ["listSubscriptions"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching subscription list",
        buildLogContext(
          "subscriptions",
          "list-subscriptions",
          userId,
          companyId,
          storeId
        )
      );

      const response: ListSubscriptionsResponse = await fetchGraphQL(
        listSubscriptionsQuery,
        { input },
        setAlert
      );

      const listResult = response?.listSubscriptions;

      // ❌ Unexpected response shape
      if (!listResult) {
        captureAppException(
          "Get subscription list failed: missing listSubscriptions",
          new Error("Get subscription list failed: missing listSubscriptions"),
          buildLogContext(
            "subscriptions",
            "list-subscriptions",
            userId,
            companyId,
            storeId
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new HandledGraphQLMutationError(
          "Get subscription list failed: missing listSubscriptions"
        );
      }

      // ⚠️ Expected API/business failure
      if (listResult.code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "List subscriptions rejected by API",
          buildLogContext(
            "subscriptions",
            "list-subscriptions",
            userId,
            companyId,
            storeId,
            { apiCode: String(listResult.code) }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: listResult.code as unknown as ALERT_BANNER_CODE
        });
        throw new HandledGraphQLMutationError(
          `List subscriptions rejected by API: ${listResult.code}`,
          listResult.code
        );
      }

      const subscriptionList = listResult.subscriptionList ?? [];

      // ✅ Success
      dispatch(setSubscriptionList(subscriptionList));

      sendAppLog(
        LOG_LEVEL.INFO,
        "Subscription list fetched successfully",
        buildLogContext(
          "subscriptions",
          "list-subscriptions",
          userId,
          companyId,
          storeId,
          { subscriptionCount: subscriptionList.length }
        )
      );

      return response;
    },

    onError: (error) => {
      if (error instanceof HandledGraphQLMutationError) return;

      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      captureAppException(
        "Failed to fetch subscription list",
        error,
        buildLogContext(
          "subscriptions",
          "list-subscriptions",
          userId,
          companyId,
          storeId
        )
      );
      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

/**
 * useGetSubscriptionDetail
 * Fetch a single subscription by ID
 */
export const useGetSubscriptionDetail = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<GetSubscriptionResponse, Error, GetSubscriptionInput>({
    mutationKey: ["getSubscription"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching subscription detail",
        buildLogContext(
          "subscriptions",
          "get-subscription",
          userId,
          companyId,
          storeId,
          { subscriptionId: String(input.subscriptionId) }
        )
      );

      const response: GetSubscriptionResponse = await fetchGraphQL(
        getSubscriptionQuery,
        { input },
        setAlert
      );

      const getResult = response?.getSubscription;

      // ❌ Unexpected response shape
      if (!getResult) {
        captureAppException(
          "Get subscription detail failed: missing getSubscription",
          new Error("Get subscription detail failed: missing getSubscription"),
          buildLogContext(
            "subscriptions",
            "get-subscription",
            userId,
            companyId,
            storeId,
            { subscriptionId: String(input.subscriptionId) }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new HandledGraphQLMutationError(
          "Get subscription detail failed: missing getSubscription"
        );
      }

      // ⚠️ Expected API/business failure
      if (getResult.code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Get subscription detail rejected by API",
          buildLogContext(
            "subscriptions",
            "get-subscription",
            userId,
            companyId,
            storeId,
            {
              subscriptionId: String(input.subscriptionId),
              apiCode: String(getResult.code)
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: getResult.code as unknown as ALERT_BANNER_CODE
        });
        throw new HandledGraphQLMutationError(
          `Get subscription detail rejected by API: ${getResult.code}`,
          getResult.code
        );
      }

      // null subscription is a valid "not found" state — dispatch as-is
      const subscription = getResult.subscription ?? null;
      dispatch(setSubscriptionDetail(subscription));

      // ✅ Success
      sendAppLog(
        LOG_LEVEL.INFO,
        "Subscription detail fetched successfully",
        buildLogContext(
          "subscriptions",
          "get-subscription",
          userId,
          companyId,
          storeId,
          { subscriptionId: String(input.subscriptionId) }
        )
      );

      return response;
    },

    onError: (error) => {
      if (error instanceof HandledGraphQLMutationError) return;

      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      captureAppException(
        "Failed to fetch subscription detail",
        error,
        buildLogContext(
          "subscriptions",
          "get-subscription",
          userId,
          companyId,
          storeId
        )
      );
      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

/**
 * useCreateSubscription
 * Create a new subscription
 */
export const useCreateSubscription = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<
    CreateSubscriptionResponse,
    Error,
    CreateSubscriptionInput
  >({
    mutationKey: ["createSubscription"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started creating subscription",
        buildLogContext(
          "subscriptions",
          "create-subscription",
          userId,
          companyId,
          storeId
        )
      );

      const response: CreateSubscriptionResponse = await fetchGraphQL(
        createSubscriptionMutation,
        { input },
        setAlert
      );

      const createResult = response?.createSubscription;

      // ❌ Unexpected response shape
      if (!createResult) {
        captureAppException(
          "Create subscription failed: missing createSubscription",
          new Error("Create subscription failed: missing createSubscription"),
          buildLogContext(
            "subscriptions",
            "create-subscription",
            userId,
            companyId,
            storeId
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new HandledGraphQLMutationError(
          "Create subscription failed: missing createSubscription"
        );
      }

      // ⚠️ Expected API/business failure
      if (createResult.code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Subscription creation rejected by API",
          buildLogContext(
            "subscriptions",
            "create-subscription",
            userId,
            companyId,
            storeId,
            { apiCode: String(createResult.code) }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: createResult.code as unknown as ALERT_BANNER_CODE
        });
        throw new HandledGraphQLMutationError(
          `Subscription creation rejected by API: ${createResult.code}`,
          createResult.code
        );
      }

      // ❌ Logically broken success response
      if (!createResult.subscriptionId) {
        captureAppException(
          "Create subscription: API returned OK but subscription data missing",
          new Error(
            "Create subscription: API returned OK but subscription data missing"
          ),
          buildLogContext(
            "subscriptions",
            "create-subscription",
            userId,
            companyId,
            storeId
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new HandledGraphQLMutationError(
          "Create subscription: API returned OK but subscription data missing"
        );
      }

      // ✅ Success
      sendAppLog(
        LOG_LEVEL.INFO,
        "Subscription created successfully",
        buildLogContext(
          "subscriptions",
          "create-subscription",
          userId,
          companyId,
          storeId,
          { subscriptionId: String(createResult.subscriptionId) }
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
        code: ALERT_BANNER_CODE.OK
      });

      return response;
    },

    onError: (error) => {
      if (error instanceof HandledGraphQLMutationError) return;

      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      captureAppException(
        "Failed to create subscription",
        error,
        buildLogContext(
          "subscriptions",
          "create-subscription",
          userId,
          companyId,
          storeId
        )
      );
      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

/**
 * useCancelSubscription
 * Cancel an existing subscription
 */
export const useCancelSubscription = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<
    CancelSubscriptionResponse,
    Error,
    CancelSubscriptionInput
  >({
    mutationKey: ["cancelSubscription"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started cancelling subscription",
        buildLogContext(
          "subscriptions",
          "cancel-subscription",
          userId,
          companyId,
          storeId,
          { subscriptionId: String(input.subscriptionId) }
        )
      );

      const response: CancelSubscriptionResponse = await fetchGraphQL(
        cancelSubscriptionMutation,
        { input },
        setAlert
      );

      const cancelResult = response?.cancelSubscription;

      // ❌ Unexpected response shape
      if (!cancelResult) {
        captureAppException(
          "Cancel subscription failed: missing cancelSubscription",
          new Error("Cancel subscription failed: missing cancelSubscription"),
          buildLogContext(
            "subscriptions",
            "cancel-subscription",
            userId,
            companyId,
            storeId,
            { subscriptionId: String(input.subscriptionId) }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new HandledGraphQLMutationError(
          "Cancel subscription failed: missing cancelSubscription"
        );
      }

      // ⚠️ Expected API/business failure
      if (cancelResult.code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Subscription cancellation rejected by API",
          buildLogContext(
            "subscriptions",
            "cancel-subscription",
            userId,
            companyId,
            storeId,
            {
              subscriptionId: String(input.subscriptionId),
              apiCode: String(cancelResult.code)
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: cancelResult.code as unknown as ALERT_BANNER_CODE
        });
        throw new HandledGraphQLMutationError(
          `Subscription cancellation rejected by API: ${cancelResult.code}`,
          cancelResult.code
        );
      }

      // null subscription body is valid for a cancel response — only update
      // Redux when the API returns an entity
      if (cancelResult.subscription) {
        dispatch(setSubscriptionDetail(cancelResult.subscription));
      }

      // ✅ Success
      sendAppLog(
        LOG_LEVEL.INFO,
        "Subscription cancelled successfully",
        buildLogContext(
          "subscriptions",
          "cancel-subscription",
          userId,
          companyId,
          storeId,
          { subscriptionId: String(input.subscriptionId) }
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
        code: ALERT_BANNER_CODE.OK
      });

      return response;
    },

    onError: (error) => {
      if (error instanceof HandledGraphQLMutationError) return;

      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      captureAppException(
        "Failed to cancel subscription",
        error,
        buildLogContext(
          "subscriptions",
          "cancel-subscription",
          userId,
          companyId,
          storeId
        )
      );
      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};
